import { useContext, useEffect, useMemo } from 'react'

import submitLoyaltyPointsBurnCalculationRequest from 'actions/loyalty/submitLoyaltyPointsBurnCalculationRequest'
import useExperiencesBatched from 'hooks/Experiences/useExperiencesBatched'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import buildBurnKey from 'loyalty/lib/pointsCalculation/buildBurnKey'
import LoyaltySpendPointsPricingContext from 'loyalty/contexts/LoyaltySpendPointsPricingContext'
import { getCashPriceCap } from './loyaltySpendPointsBurnUtils'
import type { SpendPointsSortOption } from './loyaltySpendPointsTypes'

interface Props {
  experienceIds: Array<string>
  searchFetching?: boolean
  sortOption?: SpendPointsSortOption
}

interface Result {
  experienceIds: Array<string>
  fetching: boolean
  cashPriceCap?: number
}

/**
 * Points-based filter is handled client-side because the experiences search
 * endpoint has no concept of price-in-points. Until search gains a
 * `pointsPriceLte` capability, burn prices are resolved here via the
 * burn-calc batch API and used to:
 *   1. derive a cash-price ceiling passed as `priceLte` to the search query
 *   2. filter the returned experience IDs to those with a resolved burn price,
 *      hiding offers where points pricing is unavailable
 */
function useLoyaltySpendPointsExperienceBurnFilter({
  experienceIds,
  searchFetching = false,
  sortOption,
}: Props): Result {
  const dispatch = useAppDispatch()
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')
  const burnCalculations = useAppSelector(state => state.loyalty.pointsBurnBatchCalculations)
  const { burnPointsLimit, showOffersWithinBalance } = useContext(LoyaltySpendPointsPricingContext)
  const [experiences, experiencesFetching] = useExperiencesBatched(experienceIds)

  const burnCandidates = useMemo(() => experiences.flatMap(experience => (
    experience.price ? [{
      id: experience.id,
      standard: {
        price: experience.price,
        loyaltyProductType: 'experience' as const,
      },
      member: experience.pricing.memberPrice ? {
        price: experience.pricing.memberPrice,
        loyaltyProductType: 'experience' as const,
      } : undefined,
    }] : []
  )), [experiences])

  useEffect(() => {
    for (const { standard, member } of burnCandidates) {
      dispatch(submitLoyaltyPointsBurnCalculationRequest(standard, { viewPriceInPoints: true }))
      if (member) {
        dispatch(submitLoyaltyPointsBurnCalculationRequest(member, { viewPriceInPoints: true }))
      }
    }
    // customerId is included so burn calculations are re-dispatched (under the
    // correct key) when auth loads after the initial render.
  }, [burnCandidates, customerId, dispatch])

  const pointsByExperienceId = useMemo(() => new Map(burnCandidates.map(({ id, standard, member }) => {
    const memberPoints = member
      ? burnCalculations?.[buildBurnKey(member, customerId)]?.data?.priceInPoints
      : undefined
    const standardPoints = burnCalculations?.[buildBurnKey(standard, customerId)]?.data?.priceInPoints
    return [id, memberPoints ?? standardPoints]
  })), [burnCalculations, burnCandidates, customerId])

  const isWaitingForBurnPrices = burnCandidates.length > 0
    && burnCandidates.some(({ id, standard }) => {
      const calculation = burnCalculations?.[buildBurnKey(standard, customerId)]
      return pointsByExperienceId.get(id) === undefined && !calculation?.error
    })
  const fetching = searchFetching || experiencesFetching || isWaitingForBurnPrices

  const cashPriceCap = useMemo(() => {
    if (!showOffersWithinBalance || burnPointsLimit === undefined || fetching) return undefined

    return getCashPriceCap(burnPointsLimit, burnCandidates.flatMap(({ id, standard }) => {
      const priceInPoints = pointsByExperienceId.get(id)
      return priceInPoints ? [{
        price: standard.price,
        priceInPoints,
        loyaltyProductType: 'experience' as const,
      }] : []
    }))
  }, [burnCandidates, burnPointsLimit, fetching, pointsByExperienceId, showOffersWithinBalance])

  const filteredExperienceIds = useMemo(() => {
    const experienceIdsWithBurnPrice = experienceIds.filter(id => pointsByExperienceId.get(id) !== undefined)

    const experienceIdsWithinBalance = !showOffersWithinBalance || burnPointsLimit === undefined
      ? experienceIdsWithBurnPrice
      : experienceIdsWithBurnPrice.filter(id => {
          const points = pointsByExperienceId.get(id)
          return points !== undefined && points <= burnPointsLimit
        })

    if (isWaitingForBurnPrices || experiencesFetching) {
      return []
    }

    if (searchFetching && experienceIdsWithinBalance.length === 0) {
      return []
    }

    if (sortOption === 'lowest-points' || sortOption === 'highest-points') {
      const direction = sortOption === 'lowest-points' ? 1 : -1
      return [...experienceIdsWithinBalance].sort((a, b) => {
        const aPoints = pointsByExperienceId.get(a)
        const bPoints = pointsByExperienceId.get(b)
        if (aPoints === undefined) return bPoints === undefined ? 0 : 1
        if (bPoints === undefined) return -1
        return (aPoints - bPoints) * direction
      })
    }

    return experienceIdsWithinBalance
  }, [burnPointsLimit, experienceIds, experiencesFetching, isWaitingForBurnPrices, pointsByExperienceId, searchFetching, showOffersWithinBalance, sortOption])

  return { experienceIds: filteredExperienceIds, fetching, cashPriceCap }
}

export default useLoyaltySpendPointsExperienceBurnFilter
